import { lookup } from '../content.ts';
import { type Event } from '../events.ts';
import { type Dir, isDir, samePos } from '../grid.ts';
import { assertLeaderMayAct, canMove, moveUnit, runRound } from '../round.ts';
import { type BagEntry, InvalidCommand, type RunState, floorOf, unitById } from '../state.ts';
import { type ApplyResult, type CommandHandler, type Ctx, chain, none } from './types.ts';

/**
 * The leader steps one tile in `dir` and faces it, then the round runs.
 * Blocked by walls, enemies and corner-cutting; stepping into a standing ally
 * swaps places with it. Arriving on an item picks it up
 * if the bag has room.
 */
export type MoveCommand = {
  readonly type: 'move';
  readonly dir: Dir;
};

/**
 * Merges `count` of `item` into the bag: first into existing stacks up to
 * `stackSize.dungeon`, then into new stacks while there are fewer than
 * `bagSlots`. Returns the new bag and how many fitted.
 */
const addToBag = (ctx: Ctx, bag: readonly BagEntry[], item: string, count: number): { bag: BagEntry[]; taken: number } => {
  const stack = lookup(ctx.content.items, item)?.stackSize.dungeon ?? 1;
  let left = count;
  const out = bag.map((e) => {
    if (e.item !== item || left === 0) return e;
    const add = Math.min(left, stack - e.count);
    if (add <= 0) return e;
    left -= add;
    return { ...e, count: e.count + add };
  });
  while (left > 0 && out.length < ctx.content.rules.dungeon.bagSlots) {
    const add = Math.min(left, stack);
    out.push({ item, count: add });
    left -= add;
  }
  return { bag: out, taken: count - left };
};

/**
 * Picks up the item under the unit, as much as fits (`item_picked_up` with the
 * count taken); what does not fit stays on the floor (`item_left` with the
 * count left, after any `item_picked_up`, so a full bag is never silent).
 */
const pickUp = (ctx: Ctx, state: RunState, unitId: string): ApplyResult => {
  const u = unitById(state, unitId);
  const floor = floorOf(state);
  const lying = floor.items.find((i) => samePos(i.pos, u.pos));
  if (!lying) return none(state);
  const { bag, taken } = addToBag(ctx, state.bag, lying.item, lying.count);
  const left = lying.count - taken;
  const events: Event[] = [];
  if (taken > 0) events.push({ type: 'item_picked_up', unit: unitId, item: lying.item, count: taken, pos: u.pos });
  if (left > 0) events.push({ type: 'item_left', unit: unitId, item: lying.item, count: left, pos: u.pos });
  if (taken === 0) return { state, events };
  const items = floor.items.flatMap((i) => (i === lying ? (left > 0 ? [{ ...i, count: left }] : []) : [i]));
  return { state: { ...state, bag, floor: { ...floor, items } }, events };
};

export const move: CommandHandler<MoveCommand> = (ctx, state, command) => {
  const leader = assertLeaderMayAct(state, 'other');
  if (!isDir(command.dir)) throw new InvalidCommand('bad_direction', 'bad direction');
  if (!canMove(state, leader.id, command.dir)) throw new InvalidCommand('cannot_step', 'cannot step there');
  return runRound(ctx, state, (s) =>
    chain(
      s,
      (x) => moveUnit(ctx, x, leader.id, command.dir),
      // Only an actual step picks up (a confused step may go elsewhere or nowhere).
      (x) => (samePos(unitById(x, leader.id).pos, leader.pos) ? none(x) : pickUp(ctx, x, leader.id)),
    ),
    'walk',
  );
};
